import axios from 'axios';
import { ActionContext } from 'vuex';

import api from '@/tools/api';

import { fetchQueue } from './queueActions';
import { ProductionState } from './types';

type Context = ActionContext<ProductionState, any>;

function errorMessage(err: unknown): string | undefined {
	return err instanceof Error ? err.message : undefined;
}

function errorResponse(err: unknown) {
	return axios.isAxiosError(err) ? err.response : undefined;
}

export const actions = {
	// ============================================
	// Production Materials
	// ============================================
	async fetchMaterials({ commit }: Context, filters?: any) {
		try {
			const response = await api.get('/production/materials', { params: filters });
			const responseData = response?.data || response || {};

			// Handle paginated response from Laravel
			// Backend returns: { success: true, data: { data: [...], meta: {...} } }
			let materials = [];
			let meta = null;

			if (responseData.data) {
				// Check if it's paginated response
				if (responseData.data.data && Array.isArray(responseData.data.data)) {
					// Paginated: { data: { data: [...], meta: {...} } }
					materials = responseData.data.data;
					meta = responseData.data.meta || null;
				} else if (Array.isArray(responseData.data)) {
					// Direct array: { data: [...] }
					materials = responseData.data;
				}
			} else if (Array.isArray(responseData)) {
				// Direct array response
				materials = responseData;
			}

			// Transform snake_case to camelCase
			const transformedMaterials = materials.map((material: any) => ({
				id: material.id,
				name: material.name,
				symbol: material.symbol,
				category: material.category,
				unitOfMeasure: material.unit_of_measure,
				costPerUnit: material.cost_per_unit,
				supplierName: material.supplier_name,
				notes: material.notes,
			}));

			commit('setMaterials', transformedMaterials);

			// Return pagination info if available
			if (meta) {
				return {
					materials: transformedMaterials,
					meta: meta,
					totalPages: meta.last_page || 1,
				};
			}

			return transformedMaterials;
		} catch (error) {
			console.error('fetchMaterials error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch materials');
			commit('setMaterials', []);
			throw error;
		}
	},

	async createMaterial({ commit, dispatch }: Context, materialData: any) {
		try {
			const response = await api.post('/production/materials', materialData);
			const responseData = response?.data || response || {};
			const data = responseData.data || responseData;

			// Transform snake_case to camelCase
			const transformedMaterial = {
				id: data.id,
				name: data.name,
				symbol: data.symbol,
				category: data.category,
				unitOfMeasure: data.unit_of_measure,
				costPerUnit: data.cost_per_unit,
				supplierName: data.supplier_name,
				notes: data.notes,
			};

			// Refresh materials list
			await dispatch('fetchMaterials');

			return transformedMaterial;
		} catch (error) {
			console.error('createMaterial error:', error);
			commit('setError', errorMessage(error) || 'Failed to create material');
			throw error;
		}
	},

	async updateMaterial({ commit, dispatch }: Context, { id, data }: { id: number; data: any }) {
		try {
			const response = await api.put(`/production/materials/${id}`, data);
			const responseData = response?.data || response || {};
			const materialData = responseData.data || responseData;

			// Transform snake_case to camelCase
			const transformedMaterial = {
				id: materialData.id,
				name: materialData.name,
				symbol: materialData.symbol,
				category: materialData.category,
				unitOfMeasure: materialData.unit_of_measure,
				costPerUnit: materialData.cost_per_unit,
				supplierName: materialData.supplier_name,
				notes: materialData.notes,
			};

			// Refresh materials list
			await dispatch('fetchMaterials');

			return transformedMaterial;
		} catch (error) {
			console.error('updateMaterial error:', error);
			commit('setError', errorMessage(error) || 'Failed to update material');
			throw error;
		}
	},

	async deleteMaterial({ commit, dispatch }: Context, materialId: number) {
		try {
			await api.delete(`/production/materials/${materialId}`);

			// Refresh materials list
			await dispatch('fetchMaterials');
		} catch (error) {
			console.error('deleteMaterial error:', error);
			commit('setError', errorMessage(error) || 'Failed to delete material');
			throw error;
		}
	},

	// ============================================
	// Material Norms
	// ============================================
	async fetchMaterialNormsForProduct(
		{ commit }: Context,
		{ productType, productId }: { productType: string; productId: number },
	) {
		try {
			const response = await api.get(`/production/materials/norms/${productType}/${productId}`);
			const responseData = response?.data || response || {};
			const norms = responseData.data || responseData || [];

			// Transform snake_case to camelCase
			return norms.map((norm: any) => ({
				id: norm.id,
				materialId: norm.material_id,
				quantity: norm.quantity,
				notes: norm.notes,
				material: norm.material
					? {
							id: norm.material.id,
							name: norm.material.name,
							symbol: norm.material.symbol,
							category: norm.material.category,
							unitOfMeasure: norm.material.unit_of_measure,
							costPerUnit: norm.material.cost_per_unit,
							notes: norm.material.notes,
						}
					: undefined,
			}));
		} catch (error) {
			console.error('fetchMaterialNormsForProduct error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch material norms');
			return [];
		}
	},

	async createMaterialNorm({ commit }: Context, normData: any) {
		try {
			const response = await api.post('/production/materials/norms', normData);
			const data = response?.data?.data || response?.data || response;

			// Transform response to camelCase
			return {
				id: data.id,
				materialId: data.material_id,
				quantity: data.quantity,
				notes: data.notes,
				material: data.material
					? {
							id: data.material.id,
							name: data.material.name,
							symbol: data.material.symbol,
							category: data.material.category,
							unitOfMeasure: data.material.unit_of_measure,
							costPerUnit: data.material.cost_per_unit,
							notes: data.material.notes,
						}
					: undefined,
			};
		} catch (error) {
			console.error('createMaterialNorm error:', error);
			commit('setError', errorMessage(error) || 'Failed to create material norm');
			throw error;
		}
	},

	async updateMaterialNorm({ commit }: Context, { id, data }: { id: number; data: any }) {
		try {
			const response = await api.put(`/production/materials/norms/${id}`, data);
			const responseData = response?.data?.data || response?.data || response;

			// Transform response to camelCase
			return {
				id: responseData.id,
				materialId: responseData.material_id,
				quantity: responseData.quantity,
				notes: responseData.notes,
				material: responseData.material
					? {
							id: responseData.material.id,
							name: responseData.material.name,
							symbol: responseData.material.symbol,
							category: responseData.material.category,
							unitOfMeasure: responseData.material.unit_of_measure,
							costPerUnit: responseData.material.cost_per_unit,
							notes: responseData.material.notes,
						}
					: undefined,
			};
		} catch (error) {
			console.error('updateMaterialNorm error:', error);
			commit('setError', errorMessage(error) || 'Failed to update material norm');
			throw error;
		}
	},

	async deleteMaterialNorm({ commit }: Context, normId: number) {
		try {
			await api.delete(`/production/materials/norms/${normId}`);
		} catch (error) {
			console.error('deleteMaterialNorm error:', error);
			commit('setError', errorMessage(error) || 'Failed to delete material norm');
			throw error;
		}
	},

	// ============================================
	// Order Material Usage
	// ============================================
	async fetchPlannedMaterials({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/materials/planned`);
			const responseData = response?.data || response || {};
			const data = responseData.data || responseData || {};

			// Transform to camelCase
			// Backend returns: { material_id, material: {obj}, quantity, unit_of_measure, cost_per_unit }
			const materials = (data.materials || []).map((item: any) => ({
				materialId: item.material_id,
				material: item.material, // Keep the whole material object
				quantity: item.quantity,
				unitOfMeasure: item.unit_of_measure,
				costPerUnit: item.cost_per_unit,
			}));

			commit('setPlannedMaterials', materials);
			return materials;
		} catch (error) {
			console.error('fetchPlannedMaterials error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch planned materials');
			commit('setPlannedMaterials', []);
			return [];
		}
	},

	async fetchPlannedMaterialsByProduct({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/materials/planned-by-product`);
			const responseData = response?.data || response || {};
			const data = responseData.data || responseData || {};

			// Backend returns: { products: [{product_type, product_order_id, product_name, product_sku, materials: [...]}] }
			const products = (data.products || []).map((product: any) => ({
				productType: product.product_type,
				productOrderId: product.product_order_id,
				productName: product.product_name,
				productSku: product.product_sku,
				materials: product.materials.map((item: any) => ({
					materialId: item.material_id,
					material: item.material,
					quantity: item.quantity,
					unitOfMeasure: item.unit_of_measure,
					costPerUnit: item.cost_per_unit,
				})),
			}));

			commit('setPlannedMaterialsByProduct', products);
			return products;
		} catch (error) {
			console.error('fetchPlannedMaterialsByProduct error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch planned materials by product');
			return [];
		}
	},

	async fetchMaterialUsage({ commit }: Context, queueId: number) {
		try {
			console.log('[Store] Fetching material usage for queue:', queueId);

			const response = await api.get(`/production/queue/${queueId}/materials/usage`);
			console.log('[Store] Raw response:', response);

			const responseData = response?.data || response || {};
			const data = responseData.data || responseData || {};

			console.log('[Store] Response data:', data);

			// Backend now returns camelCase via OrderMaterialUsageResource
			const usage = data.usage || [];

			console.log('[Store] Parsed usage array:', usage);

			commit('setMaterialUsage', usage);
			return usage;
		} catch (error) {
			console.error('fetchMaterialUsage error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch material usage');
			commit('setMaterialUsage', []);
			return [];
		}
	},

	async recordMaterialUsage(
		{ commit }: Context,
		{ queueId, materials }: { queueId: number; materials: any[] },
	) {
		try {
			const response = await api.post(`/production/queue/${queueId}/materials/usage`, {
				materials,
			});
			const responseData = response?.data || response || {};
			return responseData.data || responseData;
		} catch (error) {
			console.error('recordMaterialUsage error:', error);
			commit('setError', errorMessage(error) || 'Failed to record material usage');
			throw error;
		}
	},

	async updateMaterialUsage(
		{ commit }: Context,
		{ usageId, data }: { usageId: number; data: any },
	) {
		try {
			const response = await api.put(`/production/materials/usage/${usageId}`, data);
			return response?.data || response;
		} catch (error) {
			console.error('updateMaterialUsage error:', error);
			commit('setError', errorMessage(error) || 'Failed to update material usage');
			throw error;
		}
	},

	async deleteMaterialUsage({ commit }: Context, usageId: number) {
		try {
			await api.delete(`/production/materials/usage/${usageId}`);
		} catch (error) {
			console.error('deleteMaterialUsage error:', error);
			commit('setError', errorMessage(error) || 'Failed to delete material usage');
			throw error;
		}
	},

	async fetchMaterialComparisonByProduct({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(
				`/production/queue/${queueId}/materials/comparison-by-product`,
			);
			const responseData = response?.data || response || {};
			const data = responseData.data || responseData || {};

			commit('setMaterialComparisonByProduct', data);
			return data;
		} catch (error) {
			console.error('fetchMaterialComparisonByProduct error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch material comparison by product');
			return null;
		}
	},

	// ============================================
	// Products
	// ============================================
	async fetchProductById({ commit }: Context, id: number) {
		try {
			const response = await api.get(`/production/products/${id}`);
			const responseData = response?.data || response || {};
			return responseData.data || responseData;
		} catch (error) {
			console.error('fetchProductById error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch product');
			throw error;
		}
	},

	async fetchProductByTypeAndId({ commit }: Context, { type, id }: { type: string; id: number }) {
		try {
			const response = await api.get(`/production/products/${id}?type=${type}`);
			const responseData = response?.data || response || {};
			return responseData.data || responseData;
		} catch (error) {
			console.error('fetchProductByTypeAndId error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch product');
			throw error;
		}
	},

	async createProduct({ dispatch }: Context, productData: any) {
		await api.post('/production/products', productData);
		await dispatch('fetchProducts');
	},

	async updateProduct({ commit }: Context, { id, data }: { id: number; data: any }) {
		try {
			const response = await api.put(`/production/products/${id}`, data);
			const responseData = response?.data || response || {};
			commit('updateProduct', responseData.data || responseData);
		} catch (error) {
			console.error('updateProduct error:', error);
			commit('setError', errorMessage(error) || 'Failed to update product');
			throw error;
		}
	},

	async updateProductVariations(
		{ commit }: Context,
		{
			productId,
			variations,
			productType,
		}: { productId: number; variations: any; productType?: string },
	) {
		try {
			const url = productType
				? `/production/products/${productId}?type=${productType}`
				: `/production/products/${productId}`;

			const response = await api.put(url, variations);
			const responseData = response?.data || response || {};
			const updatedProduct = responseData.data || responseData;

			commit('updateProduct', updatedProduct);
			return updatedProduct;
		} catch (error) {
			console.error('updateProductVariations error:', error);
			commit('setError', errorMessage(error) || 'Failed to update product variations');
			throw error;
		}
	},

	async deleteProduct({ commit }: Context, id: number) {
		await api.delete(`/production/products/${id}`);
		commit('removeProduct', id);
	},

	// Product Variations & SKU Combinations
	async fetchSkuCombinations(
		{ commit }: Context,
		{ productId, productType }: { productId: number; productType?: string },
	) {
		try {
			const url = productType
				? `/production/products/${productId}/sku-combinations?type=${productType}`
				: `/production/products/${productId}/sku-combinations`;

			const response = await api.get(url);
			const responseData = response?.data || response || {};
			return responseData;
		} catch (error) {
			console.error('fetchSkuCombinations error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch SKU combinations');
			throw error;
		}
	},

	async fetchVariationStats(
		{ commit }: Context,
		{ productId, productType }: { productId: number; productType?: string },
	) {
		try {
			const url = productType
				? `/production/products/${productId}/variation-stats?type=${productType}`
				: `/production/products/${productId}/variation-stats`;

			const response = await api.get(url);
			const responseData = response?.data || response || {};
			return responseData;
		} catch (error) {
			console.error('fetchVariationStats error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch variation stats');
			throw error;
		}
	},

	async fetchInternalProducts({ commit }: Context) {
		try {
			const response = await api.get('/production/products/internal/list');
			const responseData = response?.data || response || {};
			return responseData.data || responseData || [];
		} catch (error) {
			console.error('fetchInternalProducts error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch internal products');
			return [];
		}
	},

	async fetchExternalProducts({ commit }: Context) {
		try {
			const response = await api.get('/production/products/external/list');
			const responseData = response?.data || response || {};
			const products = responseData.data || responseData || [];

			// Update products in state (will be filtered by getter)
			commit('setProducts', products);
			return products;
		} catch (error) {
			console.error('fetchExternalProducts error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch external products');
			return [];
		}
	},

	// ============================================
	// Production Queue
	// ============================================
	fetchQueue,

	async fetchQueueItemById({ commit }: Context, id: number) {
		try {
			const raw = await api.get(`/production/queue/${id}`);
			// Unwrap if API returns { data: {...} }
			const item = raw?.data ?? raw;

			commit('setCurrentQueueItem', item);
			return item;
		} catch (error) {
			console.error('Error in fetchQueueItemById:', error);
			console.error('Error response:', errorResponse(error));
			console.error('Error status:', errorResponse(error)?.status);
			console.error('Error data:', errorResponse(error)?.data);
			commit('setError', errorMessage(error));
			commit('setCurrentQueueItem', null);
			throw error;
		}
	},

	async addToQueue({ dispatch }: Context, orderData: any) {
		await api.post('/production/queue', orderData);
		await dispatch('fetchQueue');
	},

	async updateQueueStatus({ dispatch }: Context, { id, status }: { id: number; status: string }) {
		await api.put(`/production/queue/${id}/status`, { status });
		await dispatch('fetchQueue');
	},

	async updateQueuePriority(
		{ dispatch }: Context,
		{ id, priority }: { id: number; priority: number },
	) {
		await api.put(`/production/queue/${id}/priority`, { priority });
		await dispatch('fetchQueue');
	},

	async assignManager({ dispatch }: Context, { id, managerId }: { id: number; managerId: number }) {
		await api.post(`/production/queue/${id}/assign/${managerId}`);
		await dispatch('fetchQueue');
	},

	// ============================================
	// Timesheet
	// ============================================
	async fetchTimesheet({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/timesheet`);
			const responseData = response?.data || response || {};
			return responseData.data || responseData || [];
		} catch (error) {
			console.error('fetchTimesheet error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch timesheet');
			return [];
		}
	},

	// ============================================
	// External Assignments
	// ============================================
	async fetchExternalAssignments({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/external-assignments`);
			const responseData = response?.data || response || {};
			return responseData.data || responseData || [];
		} catch (error) {
			console.error('fetchExternalAssignments error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch external assignments');
			return [];
		}
	},

	async addExternalAssignment(
		{ dispatch }: Context,
		{ queueId, assignmentData }: { queueId: number; assignmentData: any },
	) {
		await api.post(`/production/queue/${queueId}/external-assignments`, assignmentData);
		await dispatch('fetchQueueItemById', queueId);
	},

	async updateExternalAssignment(
		{ dispatch, state }: Context,
		{ id, data }: { id: number; data: any },
	) {
		await api.put(`/production/external-assignments/${id}`, data);
		if (state.currentQueueItem) {
			await dispatch('fetchQueueItemById', state.currentQueueItem.id);
		}
	},

	async updateExternalAssignmentStatus(
		{ dispatch, state }: Context,
		{ id, status }: { id: number; status: string },
	) {
		try {
			console.log('Updating external assignment status:', { id, status });
			const response = await api.put(`/production/external-assignments/${id}/status`, { status });
			console.log('Update response:', response);

			if (state.currentQueueItem) {
				console.log('Refreshing queue item:', state.currentQueueItem.id);
				await dispatch('fetchQueueItemById', state.currentQueueItem.id);
			}
		} catch (error) {
			console.error('Error updating external assignment status:', error);
			console.error('Error response:', errorResponse(error));
			throw error;
		}
	},

	// ============================================
	// Component Requirements
	// ============================================
	async fetchComponentRequirements({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/components`);
			const responseData = response?.data || response || {};
			return responseData.data || responseData || [];
		} catch (error) {
			console.error('fetchComponentRequirements error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch component requirements');
			return [];
		}
	},

	async addComponentRequirement(
		{ dispatch }: Context,
		{ queueId, componentData }: { queueId: number; componentData: any },
	) {
		await api.post(`/production/queue/${queueId}/components`, componentData);
		await dispatch('fetchQueueItemById', queueId);
	},

	async updateComponentRequirement(
		{ dispatch, state }: Context,
		{ id, data }: { id: number; data: any },
	) {
		await api.put(`/production/components/${id}`, data);
		if (state.currentQueueItem) {
			await dispatch('fetchQueueItemById', state.currentQueueItem.id);
		}
	},

	async updateComponentRequirementStatus(
		{ dispatch, state }: Context,
		{ id, status }: { id: number; status: string },
	) {
		try {
			console.log('Updating component requirement status:', { id, status });
			const response = await api.put(`/production/components/${id}/status`, { status });
			console.log('Update response:', response);

			if (state.currentQueueItem) {
				console.log('Refreshing queue item:', state.currentQueueItem.id);
				await dispatch('fetchQueueItemById', state.currentQueueItem.id);
			}
		} catch (error) {
			console.error('Error updating component requirement status:', error);
			console.error('Error response:', errorResponse(error));
			throw error;
		}
	},

	async fetchPendingComponents({ commit }: Context) {
		try {
			const response = await api.get('/production/components/pending');
			const responseData = response?.data || response || {};
			return responseData.data || responseData || [];
		} catch (error) {
			console.error('fetchPendingComponents error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch pending components');
			return [];
		}
	},

	// ============================================
	// Timeline
	// ============================================
	async fetchTimeline({ commit }: Context) {
		commit('setLoading', true);

		try {
			const response = await api.get('/production/timeline');
			const responseData = response?.data || response || [];
			// Backend zwraca snake_case; widok używa camelCase.
			const mapped = Array.isArray(responseData)
				? responseData.map((it: any) => ({
						id: it.id,
						orderNumber: it.order_number ?? it.orderNumber ?? '',
						startDate: it.start_date ?? it.startDate ?? null,
						endDate: it.end_date ?? it.endDate ?? null,
						actualStart: it.actual_start ?? it.actualStart ?? null,
						actualEnd: it.actual_end ?? it.actualEnd ?? null,
						status: it.status ?? 'pending',
						priority: it.priority ?? 0,
						manager: it.manager ?? null,
						progress: it.progress ?? undefined,
						totalProducts: it.total_products ?? undefined,
						completedProducts: it.completed_products ?? undefined,
						estimatedHours: it.estimated_hours ?? undefined,
						spentHours: it.spent_hours ?? undefined,
						predictedCompletionDate: it.predicted_completion_date ?? null,
					}))
				: [];
			commit('setTimeline', mapped);
		} catch (error) {
			commit('setError', errorMessage(error));
			throw error;
		} finally {
			commit('setLoading', false);
		}
	},

	// ============================================
	// Suppliers
	// ============================================
	async fetchSuppliers({ commit }: Context) {
		commit('setLoading', true);
		commit('setError', null);

		try {
			const response = await api.get('/production/suppliers');

			// Safe access to response data
			const responseData = response?.data || response || {};

			commit('setSuppliers', responseData.data || responseData || []);
		} catch (error) {
			console.error('fetchSuppliers error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch suppliers');
			commit('setSuppliers', []); // Set empty array on error
		} finally {
			commit('setLoading', false);
		}
	},

	async createSupplier({ dispatch }: Context, supplierData: any) {
		try {
			await api.post('/production/suppliers', supplierData);
			await dispatch('fetchSuppliers');
		} catch (error) {
			console.error('createSupplier error:', error);
			throw error;
		}
	},

	async updateSupplier({ dispatch }: Context, { id, data }: { id: number; data: any }) {
		try {
			await api.put(`/production/suppliers/${id}`, data);
			await dispatch('fetchSuppliers');
		} catch (error) {
			console.error('updateSupplier error:', error);
			throw error;
		}
	},

	async deleteSupplier({ dispatch }: Context, id: number) {
		try {
			await api.delete(`/production/suppliers/${id}`);
			await dispatch('fetchSuppliers');
		} catch (error) {
			console.error('deleteSupplier error:', error);
			throw error;
		}
	},

	// ============================================
	// Production Stages
	// ============================================
	async fetchProductionStages({ commit }: Context) {
		// Assuming there's an endpoint for this, or we can hardcode the stages
		// For now, hardcoded based on seeder
		const stages = [
			{ id: 1, name: 'Model', slug: 'model', orderPosition: 1, isActive: true },
			{ id: 2, name: 'FDM Print', slug: 'fdm_print', orderPosition: 2, isActive: true },
			{ id: 3, name: 'MJF Print', slug: 'mjf_print', orderPosition: 3, isActive: true },
			{ id: 4, name: 'Sandblast', slug: 'sandblast', orderPosition: 4, isActive: true },
			{ id: 5, name: 'Assembly', slug: 'assembly', orderPosition: 5, isActive: true },
		];

		commit('setProductionStages', stages);
	},

	// ============================================
	// Users/Employees
	// ============================================
	async fetchUsers({ commit }: Context) {
		try {
			console.log('Fetching users from /production/users');
			const response = await api.get('/production/users');
			console.log('Users response:', response);

			// Response is already parsed by axios interceptor
			const users = Array.isArray(response) ? response : [];
			console.log('Parsed users:', users);

			if (users.length === 0) {
				console.warn('No production managers or admins returned from API, using fallback');
				// Fallback users mają tylko role production_manager i administrator
				const fallbackUsers = [
					{ id: 3, name: 'Anna Nowak', email: 'anna.nowak@example.com', role: 'administrator' },
					{
						id: 4,
						name: 'Piotr Wiśniewski',
						email: 'piotr.wisniewski@example.com',
						role: 'production_manager',
					},
				];
				commit('setUsers', fallbackUsers);
			} else {
				commit('setUsers', users);
			}
		} catch (error) {
			console.error('fetchUsers error:', error);
			console.warn('Production users endpoint failed, using fallback users');

			// Fallback to hardcoded users for development
			// UWAGA: Fallback users mają tylko role production_manager i administrator
			const fallbackUsers = [
				{ id: 3, name: 'Anna Nowak', email: 'anna.nowak@example.com', role: 'administrator' },
				{
					id: 4,
					name: 'Piotr Wiśniewski',
					email: 'piotr.wisniewski@example.com',
					role: 'production_manager',
				},
			];

			commit('setUsers', fallbackUsers);
			commit('setError', errorMessage(error) || 'Failed to fetch users');
		}
	},

	// ============================================
	// Order Products Management
	// ============================================
	async fetchOrderProducts({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/order-products`);
			const responseData = response?.data || response || {};
			return responseData;
		} catch (error) {
			console.error('fetchOrderProducts error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch order products');
			return { products: [] };
		}
	},

	async updateOrderProductStatus(
		{ dispatch, state }: Context,
		{
			type,
			orderProductId,
			status,
			employeeId,
			notes,
		}: {
			type: string;
			orderProductId: number;
			status: string;
			employeeId?: number;
			notes?: string;
		},
	) {
		try {
			const data: any = { status };
			if (employeeId !== undefined) data.employee_id = employeeId;
			if (notes !== undefined) data.notes = notes;

			await api.put(`/production/order-products/${type}/${orderProductId}/status`, data);

			if (state.currentQueueItem) {
				await dispatch('fetchQueueItemById', state.currentQueueItem.id);
			}
		} catch (error) {
			console.error('Error updating order product status:', error);
			throw error;
		}
	},

	async assignEmployeeToOrderProduct(
		{ dispatch, state }: Context,
		{
			type,
			orderProductId,
			employeeId,
			notes,
		}: {
			type: string;
			orderProductId: number;
			employeeId: number;
			notes?: string;
		},
	) {
		try {
			const data: any = { employee_id: employeeId };
			if (notes !== undefined) data.notes = notes;

			await api.put(`/production/order-products/${type}/${orderProductId}/assign`, data);

			if (state.currentQueueItem) {
				await dispatch('fetchQueueItemById', state.currentQueueItem.id);
			}
		} catch (error) {
			console.error('Error assigning employee to order product:', error);
			throw error;
		}
	},

	// ============================================
	// Order Components (extracted from products)
	// ============================================
	async fetchOrderComponents({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/order-components`);
			const responseData = response?.data || response || {};
			return responseData;
		} catch (error) {
			console.error('fetchOrderComponents error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch order components');
			return { components: [] };
		}
	},

	// ============================================
	// Auxiliary Components
	// ============================================
	async fetchAuxiliaryComponents({ commit }: Context, filters: any = {}) {
		try {
			const params = new URLSearchParams();
			if (filters.category) params.append('category', filters.category);
			if (filters.is_internal !== undefined && filters.is_internal !== null) {
				params.append('is_internal', filters.is_internal.toString());
			}
			if (filters.supplier_id) params.append('supplier_id', filters.supplier_id.toString());
			if (filters.search) params.append('search', filters.search);
			if (filters.per_page) params.append('per_page', filters.per_page.toString());
			if (filters.sort_by) params.append('sort_by', filters.sort_by);
			if (filters.sort_order) params.append('sort_order', filters.sort_order);

			const response = await api.get(`/production/auxiliary-components?${params.toString()}`);

			// Check if response is paginated object or direct array
			let components = [];
			let paginationMeta = {};

			if (response?.current_page && response?.data && Array.isArray(response.data)) {
				// Direct pagination object (no wrapper)
				components = response.data;
				paginationMeta = {
					total: response.total,
					per_page: response.per_page,
					current_page: response.current_page,
				};
			} else if (
				response?.data?.current_page &&
				response?.data?.data &&
				Array.isArray(response.data.data)
			) {
				// Wrapped pagination
				components = response.data.data;
				paginationMeta = {
					total: response.data.total,
					per_page: response.data.per_page,
					current_page: response.data.current_page,
				};
			} else if (Array.isArray(response?.data)) {
				// Direct array response
				components = response.data;
				paginationMeta = { total: components.length, per_page: components.length, current_page: 1 };
			} else if (Array.isArray(response)) {
				// Response IS the array
				components = response;
				paginationMeta = { total: components.length, per_page: components.length, current_page: 1 };
			}

			// Transform snake_case to camelCase
			const transformedComponents = components.map((comp: any) => ({
				id: comp.id,
				name: comp.name,
				sku: comp.sku,
				componentCategory: comp.component_category,
				isInternal: comp.is_internal,
				supplierId: comp.supplier_id,
				supplierSymbol: comp.supplier_symbol,
				purchaseCost: comp.purchase_cost,
				weightGrams: comp.weight_grams,
				description: comp.description,
				specifications: comp.specifications,
				isActive: comp.is_active,
				supplier: comp.supplier,
				createdAt: comp.created_at,
				updatedAt: comp.updated_at,
			}));

			commit('setAuxiliaryComponents', transformedComponents);

			return { data: components, ...paginationMeta };
		} catch (error) {
			console.error('fetchAuxiliaryComponents error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch auxiliary components');
			commit('setAuxiliaryComponents', []);
			return { data: [], total: 0 };
		}
	},

	async createAuxiliaryComponent({ commit }: Context, componentData: any) {
		try {
			const response = await api.post('/production/auxiliary-components', componentData);
			const comp = response?.data?.component || response?.data || response;

			// Transform snake_case to camelCase
			const transformedComponent = {
				id: comp.id,
				name: comp.name,
				sku: comp.sku,
				componentCategory: comp.component_category,
				isInternal: comp.is_internal,
				supplierId: comp.supplier_id,
				supplierSymbol: comp.supplier_symbol,
				purchaseCost: comp.purchase_cost,
				weightGrams: comp.weight_grams,
				description: comp.description,
				specifications: comp.specifications,
				isActive: comp.is_active,
				supplier: comp.supplier,
				createdAt: comp.created_at,
				updatedAt: comp.updated_at,
			};

			commit('addAuxiliaryComponent', transformedComponent);
			return transformedComponent;
		} catch (error) {
			console.error('createAuxiliaryComponent error:', error);
			throw error;
		}
	},

	async updateAuxiliaryComponent({ commit }: Context, { id, data }: { id: number; data: any }) {
		try {
			const response = await api.put(`/production/auxiliary-components/${id}`, data);
			const comp = response?.data?.component || response?.data || response;

			// Transform snake_case to camelCase
			const transformedComponent = {
				id: comp.id,
				name: comp.name,
				sku: comp.sku,
				componentCategory: comp.component_category,
				isInternal: comp.is_internal,
				supplierId: comp.supplier_id,
				supplierSymbol: comp.supplier_symbol,
				purchaseCost: comp.purchase_cost,
				weightGrams: comp.weight_grams,
				description: comp.description,
				specifications: comp.specifications,
				isActive: comp.is_active,
				supplier: comp.supplier,
				createdAt: comp.created_at,
				updatedAt: comp.updated_at,
			};

			commit('updateAuxiliaryComponent', transformedComponent);
			return transformedComponent;
		} catch (error) {
			console.error('updateAuxiliaryComponent error:', error);
			throw error;
		}
	},

	async deleteAuxiliaryComponent({ commit }: Context, id: number) {
		try {
			await api.delete(`/production/auxiliary-components/${id}`);
			commit('removeAuxiliaryComponent', id);
		} catch (error) {
			console.error('deleteAuxiliaryComponent error:', error);
			throw error;
		}
	},

	// ============================================
	// Component Catalog (Unified view)
	// ============================================
	async fetchAllComponents({ commit }: Context, filters: any = {}) {
		try {
			const params = new URLSearchParams();
			if (filters.type) params.append('type', filters.type);
			if (filters.category) params.append('category', filters.category);
			if (filters.is_internal !== undefined) params.append('is_internal', filters.is_internal);
			if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
			if (filters.search) params.append('search', filters.search);
			if (filters.per_page) params.append('per_page', filters.per_page);

			const response = await api.get(`/production/components?${params.toString()}`);
			const components = response?.data?.data || response?.data || [];

			commit('setAllComponents', components);
			return response?.data || response;
		} catch (error) {
			console.error('fetchAllComponents error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch components');
			return { data: [], total: 0 };
		}
	},

	async fetchMainProducts({ commit }: Context, filters: any = {}) {
		try {
			const params = new URLSearchParams();
			if (filters.is_internal !== undefined) params.append('is_internal', filters.is_internal);
			if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);

			const response = await api.get(`/production/components/main?${params.toString()}`);
			return response?.data || response;
		} catch (error) {
			console.error('fetchMainProducts error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch main products');
			return { components: [], total: 0 };
		}
	},

	async fetchExternalComponents({ commit }: Context) {
		try {
			const response = await api.get('/production/components/external');
			return response?.data || response;
		} catch (error) {
			console.error('fetchExternalComponents error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch external components');
			return { components: [], total: 0 };
		}
	},

	async searchComponents({ commit }: Context, query: string) {
		try {
			const response = await api.get(
				`/production/components/search?q=${encodeURIComponent(query)}`,
			);
			return response?.data || response;
		} catch (error) {
			console.error('searchComponents error:', error);
			commit('setError', errorMessage(error) || 'Failed to search components');
			return { components: [], total: 0 };
		}
	},

	async fetchComponentStats({ commit }: Context, { type, id }: { type: string; id: number }) {
		try {
			const response = await api.get(`/production/components/${type}/${id}/stats`);
			return response?.data?.stats || response?.data || response;
		} catch (error) {
			console.error('fetchComponentStats error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch component stats');
			return null;
		}
	},

	// ============================================
	// BOM Rules
	// ============================================
	async fetchProductBom(
		{ commit }: Context,
		{ productType, productId }: { productType: string; productId: number },
	) {
		try {
			const response = await api.get(`/production/bom/${productType}/${productId}`);
			return response?.data || response;
		} catch (error) {
			console.error('fetchProductBom error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch BOM rules');
			return { bom_rules: [], total: 0 };
		}
	},

	async addBomRule(
		{ commit }: Context,
		{
			productType,
			productId,
			ruleData,
		}: {
			productType: string;
			productId: number;
			ruleData: any;
		},
	) {
		try {
			const response = await api.post(`/production/bom/${productType}/${productId}`, ruleData);
			return response?.data?.bom_rule || response?.data || response;
		} catch (error) {
			console.error('addBomRule error:', error);
			throw error;
		}
	},

	async updateBomRule({ commit }: Context, { bomRuleId, data }: { bomRuleId: number; data: any }) {
		try {
			const response = await api.put(`/production/bom/${bomRuleId}`, data);
			return response?.data?.bom_rule || response?.data || response;
		} catch (error) {
			console.error('updateBomRule error:', error);
			throw error;
		}
	},

	async deleteBomRule({ commit }: Context, bomRuleId: number) {
		try {
			await api.delete(`/production/bom/${bomRuleId}`);
		} catch (error) {
			console.error('deleteBomRule error:', error);
			throw error;
		}
	},

	// ============================================
	// Timesheet Comparison
	// ============================================
	async fetchTimesheetComparison({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/timesheet/compare`);
			const data = response?.data || response || [];
			return data;
		} catch (error) {
			console.error('fetchTimesheetComparison error:', error);
			return [];
		}
	},

	async fetchTimesheetComparisonByProduct({ commit }: Context, queueId: number) {
		try {
			const response = await api.get(`/production/queue/${queueId}/timesheet/compare-by-product`);
			const data = response?.data || response || {};

			// Transform snake_case to camelCase
			if (data.products && Array.isArray(data.products)) {
				data.products = data.products.map((product: any) => ({
					productType: product.product_type,
					productOrderId: product.product_order_id,
					productName: product.product_name,
					productSku: product.product_sku,
					stages:
						product.stages?.map((stage: any) => ({
							stageId: stage.stageId,
							stageName: stage.stageName,
							estimatedHours: stage.estimatedHours,
							actualHours: stage.actualHours,
							variance: stage.variance,
							variancePercent: stage.variancePercent,
						})) || [],
					summary: {
						totalEstimatedHours: product.summary?.total_estimated_hours || 0,
						totalActualHours: product.summary?.total_actual_hours || 0,
						totalVariance: product.summary?.total_variance || 0,
						totalVariancePercent: product.summary?.total_variance_percent || 0,
						stagesCount: product.summary?.stages_count || 0,
					},
				}));
			}

			if (data.overall_summary) {
				data.overallSummary = {
					totalEstimatedHours: data.overall_summary.total_estimated_hours || 0,
					totalActualHours: data.overall_summary.total_actual_hours || 0,
					totalVariance: data.overall_summary.total_variance || 0,
					totalVariancePercent: data.overall_summary.total_variance_percent || 0,
					productsCount: data.overall_summary.products_count || 0,
				};
				delete data.overall_summary;
			}

			return data;
		} catch (error) {
			console.error('fetchTimesheetComparisonByProduct error:', error);
			return { products: [], overallSummary: null };
		}
	},

	async addTimesheetEntry(
		{ commit }: Context,
		{ queueId, entryData }: { queueId: number; entryData: any },
	) {
		try {
			const response = await api.post(`/production/queue/${queueId}/timesheet`, entryData);
			return response?.data || response;
		} catch (error) {
			console.error('addTimesheetEntry error:', error);
			throw error;
		}
	},

	async updateTimesheetEntry({ commit }: Context, { id, data }: { id: number; data: any }) {
		try {
			const response = await api.put(`/production/timesheet/${id}`, data);
			return response?.data || response;
		} catch (error) {
			console.error('updateTimesheetEntry error:', error);
			throw error;
		}
	},

	async deleteTimesheetEntry({ commit }: Context, id: number) {
		try {
			await api.delete(`/production/timesheet/${id}`);
		} catch (error) {
			console.error('deleteTimesheetEntry error:', error);
			throw error;
		}
	},

	// ============================================
	// Products Management
	// ============================================
	async fetchProducts({ commit }: Context, filters: any = {}) {
		try {
			commit('setLoading', true);

			const params = new URLSearchParams();

			if (filters.search) {
				params.append('search', filters.search);
			}
			if (filters.product_type) {
				params.append('product_type', filters.product_type);
			}
			if (
				filters.is_internal !== undefined &&
				filters.is_internal !== null &&
				filters.is_internal !== ''
			) {
				params.append('is_internal', filters.is_internal.toString());
			}
			if (filters.supplier_id) {
				params.append('supplier_id', filters.supplier_id.toString());
			}

			const url = params.toString()
				? `/production/products?${params.toString()}`
				: '/production/products';

			const response = await api.get(url);
			const products = response?.data || response || [];

			commit('setProducts', products);
			commit('setLoading', false);

			return products;
		} catch (error) {
			console.error('fetchProducts error:', error);
			commit('setLoading', false);
			throw error;
		}
	},

	// ============================================
	// Filters
	// ============================================
	setFilters({ commit }: Context, filters: any) {
		commit('setFilters', filters);
	},

	clearFilters({ commit }: Context) {
		commit('clearFilters');
	},

	setComponentFilters({ commit }: Context, filters: any) {
		commit('setComponentFilters', filters);
	},

	clearComponentFilters({ commit }: Context) {
		commit('clearComponentFilters');
	},

	// ============================================
	// Production Norms (Time)
	// ============================================
	async fetchProductionNormsForProduct(
		{ commit }: Context,
		{ productType, productId }: { productType: string; productId: number },
	) {
		try {
			const response = await api.get(`/production/norms/${productType}/${productId}`);
			const responseData = response?.data || response || {};
			const norms = responseData.data || responseData || [];

			// Transform snake_case to camelCase
			return norms.map((norm: any) => ({
				id: norm.id,
				productionStageId: norm.production_stage_id,
				estimatedHours: norm.estimated_hours,
				minHours: norm.min_hours,
				maxHours: norm.max_hours,
				appliesToVariant: norm.applies_to_variant,
				notes: norm.notes,
				prosthesisId: norm.prosthesis_id,
				terminalId: norm.terminal_id,
				elbowId: norm.elbow_id,
				productionStage: norm.production_stage
					? {
							id: norm.production_stage.id,
							name: norm.production_stage.name,
							slug: norm.production_stage.slug,
							description: norm.production_stage.description,
							orderPosition: norm.production_stage.order_position,
							isActive: norm.production_stage.is_active,
						}
					: null,
			}));
		} catch (error) {
			console.error('fetchProductionNormsForProduct error:', error);
			commit('setError', errorMessage(error) || 'Failed to fetch production norms');
			return [];
		}
	},

	async createProductionNorm({ commit }: Context, normData: any) {
		try {
			const response = await api.post('/production/norms', normData);
			const data = response?.data?.data || response?.data || response;

			// Transform response to camelCase
			return {
				id: data.id,
				productionStageId: data.production_stage_id,
				estimatedHours: data.estimated_hours,
				minHours: data.min_hours,
				maxHours: data.max_hours,
				appliesToVariant: data.applies_to_variant,
				notes: data.notes,
				prosthesisId: data.prosthesis_id,
				terminalId: data.terminal_id,
				elbowId: data.elbow_id,
				productionStage: data.production_stage
					? {
							id: data.production_stage.id,
							name: data.production_stage.name,
							slug: data.production_stage.slug,
							description: data.production_stage.description,
							orderPosition: data.production_stage.order_position,
							isActive: data.production_stage.is_active,
						}
					: null,
			};
		} catch (error) {
			console.error('createProductionNorm error:', error);
			commit('setError', errorMessage(error) || 'Failed to create production norm');
			throw error;
		}
	},

	async updateProductionNorm({ commit }: Context, { id, data }: { id: number; data: any }) {
		try {
			const response = await api.put(`/production/norms/${id}`, data);
			const responseData = response?.data?.data || response?.data || response;

			// Transform response to camelCase
			return {
				id: responseData.id,
				productionStageId: responseData.production_stage_id,
				estimatedHours: responseData.estimated_hours,
				minHours: responseData.min_hours,
				maxHours: responseData.max_hours,
				appliesToVariant: responseData.applies_to_variant,
				notes: responseData.notes,
				prosthesisId: responseData.prosthesis_id,
				terminalId: responseData.terminal_id,
				elbowId: responseData.elbow_id,
				productionStage: responseData.production_stage
					? {
							id: responseData.production_stage.id,
							name: responseData.production_stage.name,
							slug: responseData.production_stage.slug,
							description: responseData.production_stage.description,
							orderPosition: responseData.production_stage.order_position,
							isActive: responseData.production_stage.is_active,
						}
					: null,
			};
		} catch (error) {
			console.error('updateProductionNorm error:', error);
			commit('setError', errorMessage(error) || 'Failed to update production norm');
			throw error;
		}
	},

	async deleteProductionNorm({ commit }: Context, normId: number) {
		try {
			await api.delete(`/production/norms/${normId}`);
		} catch (error) {
			console.error('deleteProductionNorm error:', error);
			commit('setError', errorMessage(error) || 'Failed to delete production norm');
			throw error;
		}
	},

	// ============================================
	// Production Reports
	// ============================================
	async fetchEfficiencyReport({ commit }: Context, filters: any) {
		try {
			commit('setReportsLoading', true);
			commit('setReportsError', null);

			const response = await api.get('/production/reports/efficiency', { params: filters });
			const responseData = response?.data || response || {};
			const reportData = responseData.data || responseData;

			// Transform snake_case to camelCase
			const report = {
				period: reportData.period,
				summary: {
					totalOrders: reportData.summary?.total_orders || 0,
					completedOrders: reportData.summary?.completed_orders || 0,
					avgCompletionTimeDays: reportData.summary?.avg_completion_time_days || 0,
					onTimeRatePercent: reportData.summary?.on_time_rate_percent || 0,
					avgTimeVariancePercent: reportData.summary?.avg_time_variance_percent || 0,
				},
				statusDistribution: reportData.status_distribution || {},
				avgTimePerStatus: reportData.avg_time_per_status || {},
				topLongestCompletions: (reportData.top_longest_completions || []).map((item: any) => ({
					queueId: item.queue_id,
					orderNumber: item.order_number,
					estimatedDate: item.estimated_date,
					actualDate: item.actual_date,
					daysDifference: item.days_difference,
				})),
				topShortestCompletions: (reportData.top_shortest_completions || []).map((item: any) => ({
					queueId: item.queue_id,
					orderNumber: item.order_number,
					estimatedDate: item.estimated_date,
					actualDate: item.actual_date,
					daysDifference: item.days_difference,
				})),
				filtersApplied: reportData.filters_applied || {},
			};

			commit('setEfficiencyReport', report);
			return report;
		} catch (error) {
			console.error('fetchEfficiencyReport error:', error);
			commit('setReportsError', errorMessage(error) || 'Failed to fetch efficiency report');
			commit('setEfficiencyReport', null);
			throw error;
		} finally {
			commit('setReportsLoading', false);
		}
	},

	async fetchTimeAnalysisReport({ commit }: Context, filters: any) {
		try {
			commit('setReportsLoading', true);
			commit('setReportsError', null);

			const response = await api.get('/production/reports/time-analysis', { params: filters });
			const responseData = response?.data || response || {};
			const reportData = responseData.data || responseData;

			// Transform snake_case to camelCase
			const report = {
				period: reportData.period,
				summary: {
					totalHoursWorked: reportData.summary?.total_hours_worked || 0,
					totalEntries: reportData.summary?.total_entries || 0,
				},
				actualVsEstimated: {
					actualHours: reportData.actual_vs_estimated?.actual_hours || 0,
					estimatedHours: reportData.actual_vs_estimated?.estimated_hours || 0,
					variancePercent: reportData.actual_vs_estimated?.variance_percent || 0,
				},
				variancePerStage: (reportData.variance_per_stage || []).map((item: any) => ({
					stageId: item.stage_id,
					stageName: item.stage_name,
					actualHours: item.actual_hours,
					estimatedHours: item.estimated_hours,
					variancePercent: item.variance_percent,
					entriesCount: item.entries_count,
				})),
				avgTimePerProduct: (reportData.avg_time_per_product || []).map((item: any) => ({
					productType: item.product_type,
					productOrderId: item.product_order_id,
					totalHours: item.total_hours,
					avgHours: item.avg_hours,
					entriesCount: item.entries_count,
				})),
				avgTimePerEmployee: (reportData.avg_time_per_employee || []).map((item: any) => ({
					employeeId: item.employee_id,
					employeeName: item.employee_name,
					totalHours: item.total_hours,
					avgHoursPerEntry: item.avg_hours_per_entry,
					entriesCount: item.entries_count,
				})),
				topLongestOrders: (reportData.top_longest_orders || []).map((item: any) => ({
					queueId: item.queue_id,
					orderNumber: item.order_number,
					status: item.status,
					daysRunning: item.days_running,
					createdAt: item.created_at,
				})),
				filtersApplied: reportData.filters_applied || {},
			};

			commit('setTimeAnalysisReport', report);
			return report;
		} catch (error) {
			console.error('fetchTimeAnalysisReport error:', error);
			commit('setReportsError', errorMessage(error) || 'Failed to fetch time analysis report');
			commit('setTimeAnalysisReport', null);
			throw error;
		} finally {
			commit('setReportsLoading', false);
		}
	},

	async fetchCostAnalysisReport({ commit }: Context, filters: any) {
		try {
			commit('setReportsLoading', true);
			commit('setReportsError', null);

			const response = await api.get('/production/reports/cost-analysis', { params: filters });
			const responseData = response?.data || response || {};
			const reportData = responseData.data || responseData;

			// Transform snake_case to camelCase
			const report = {
				period: reportData.period,
				summary: {
					totalCosts: reportData.summary?.total_costs || 0,
					materialCosts: reportData.summary?.material_costs || 0,
					componentCosts: reportData.summary?.component_costs || 0,
					externalCosts: reportData.summary?.external_costs || 0,
					totalOrders: reportData.summary?.total_orders || 0,
					avgCostPerOrder: reportData.summary?.avg_cost_per_order || 0,
				},
				avgCostPerProduct: (reportData.avg_cost_per_product || []).map((item: any) => ({
					productType: item.product_type,
					productOrderId: item.product_order_id,
					avgCost: item.avg_cost,
					totalCost: item.total_cost,
				})),
				topExpensiveOrders: (reportData.top_expensive_orders || []).map((item: any) => ({
					queueId: item.queue_id,
					orderNumber: item.order_number,
					totalCost: item.total_cost,
					materialCost: item.material_cost,
					componentCost: item.component_cost,
					externalCost: item.external_cost,
				})),
				filtersApplied: reportData.filters_applied || {},
			};

			commit('setCostAnalysisReport', report);
			return report;
		} catch (error) {
			console.error('fetchCostAnalysisReport error:', error);
			commit('setReportsError', errorMessage(error) || 'Failed to fetch cost analysis report');
			commit('setCostAnalysisReport', null);
			throw error;
		} finally {
			commit('setReportsLoading', false);
		}
	},

	async fetchStatusOverviewReport({ commit }: Context, filters: any) {
		try {
			commit('setReportsLoading', true);
			commit('setReportsError', null);

			const response = await api.get('/production/reports/status-overview', { params: filters });
			const responseData = response?.data || response || {};
			const reportData = responseData.data || responseData;

			// Transform snake_case to camelCase
			const report = {
				period: reportData.period,
				summary: {
					totalOrders: reportData.summary?.total_orders || 0,
					overdueCount: reportData.summary?.overdue_count || 0,
					pendingLongTimeCount: reportData.summary?.pending_long_time_count || 0,
					inProgressCount: reportData.summary?.in_progress_count || 0,
					completedCount: reportData.summary?.completed_count || 0,
				},
				statusDistribution: reportData.status_distribution || {},
				avgTimePerStatus: reportData.avg_time_per_status || {},
				overdueByStatus: reportData.overdue_by_status || {},
				overdueOrders: (reportData.overdue_orders || []).map((item: any) => ({
					queueId: item.queue_id,
					orderNumber: item.order_number,
					status: item.status,
					estimatedCompletionDate: item.estimated_completion_date,
					daysOverdue: item.days_overdue,
				})),
				pendingLongTimeOrders: (reportData.pending_long_time_orders || []).map((item: any) => ({
					queueId: item.queue_id,
					orderNumber: item.order_number,
					createdAt: item.created_at,
					daysPending: item.days_pending,
				})),
				statusTrends: (reportData.status_trends || []).map((item: any) => ({
					date: item.date,
					statusCounts: item.status_counts || {},
					total: item.total || 0,
				})),
				filtersApplied: reportData.filters_applied || {},
			};

			commit('setStatusOverviewReport', report);
			return report;
		} catch (error) {
			console.error('fetchStatusOverviewReport error:', error);
			commit('setReportsError', errorMessage(error) || 'Failed to fetch status overview report');
			commit('setStatusOverviewReport', null);
			throw error;
		} finally {
			commit('setReportsLoading', false);
		}
	},
};

export default actions;
